/**
 * ScamBlocker AI — Personel Yönetimi (/staff)
 * Org staff listele/ekle/sil. RBAC: can_manage_members (owner/admin yazma).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, UserCog } from 'lucide-react';
import { useState } from 'react';
import styles from '@/features/_shared/page.module.css';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';

interface Staff {
  id: string;
  email: string;
  full_name?: string;
  role: string;
  status: string;
  last_login_at?: string;
}

const ROLES = ['owner', 'admin', 'operator', 'viewer'];

export function StaffPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', password: '', full_name: '', role: 'operator' });
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading, isError } = useQuery<{ staff: Staff[] }>({
    queryKey: ['staff'],
    queryFn: async () => (await apiClient.get(API.org.staff)).data,
  });

  const createMut = useMutation({
    mutationFn: async () => (await apiClient.post(API.org.staff, form)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      setForm({ email: '', password: '', full_name: '', role: 'operator' });
      setShowForm(false);
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      window.alert(e.response?.data?.error ?? 'Personel eklenemedi'),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(API.org.staffById(id))).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff'] }),
  });

  const list = data?.staff ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <UserCog size={22} className={styles.titleIcon} />
        <h1 className={styles.title}>Personel</h1>
        {data && <span className={styles.countPill}>{list.length}</span>}
        <span className={styles.spacer} />
        <button type="button" className={styles.actionBtn} onClick={() => setShowForm((v) => !v)}>
          <Plus size={14} /> Personel Ekle
        </button>
      </div>

      {showForm && (
        <div className={styles.formRow}>
          <input
            className={styles.input}
            placeholder="E-posta"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            className={styles.input}
            type="password"
            placeholder="Şifre"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <input
            className={styles.input}
            placeholder="Ad Soyad"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
          <select
            className={styles.select}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={createMut.isPending || !form.email || !form.password}
            onClick={() => createMut.mutate()}
          >
            Kaydet
          </button>
        </div>
      )}

      <div className={styles.tableWrapper}>
        {isLoading && (
          <div className={styles.center}>
            <span className={styles.spinner} />
          </div>
        )}
        {isError && <div className={styles.errorBox}>Personel yüklenemedi.</div>}
        {!isLoading && !isError && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Ad</th>
                <th>E-posta</th>
                <th>Rol</th>
                <th>Durum</th>
                <th>Son Giriş</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className={styles.row}>
                  <td>{s.full_name ?? '—'}</td>
                  <td className={styles.mono}>{s.email}</td>
                  <td>
                    <span className={styles.badge} style={{ color: 'var(--color-primary)' }}>
                      {s.role}
                    </span>
                  </td>
                  <td>{s.status}</td>
                  <td className={styles.mono}>
                    {s.last_login_at ? new Date(s.last_login_at).toLocaleDateString('tr-TR') : '—'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.dangerBtn}`}
                      onClick={() =>
                        window.confirm(`${s.email} silinsin mi?`) && delMut.mutate(s.id)
                      }
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.empty}>
                    Personel yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
